(() => {
  const SELECTOR = '[data-horizontal-wheel-scroll], .js-horizontal-wheel-scroll';
  const DRAG_THRESHOLD = 8;

  const initHorizontalWheelScroll = (root = document) => {
    const elements = root.querySelectorAll(SELECTOR);

    const updateButtonsState = (container, prevButton, nextButton) => {
      if (!prevButton && !nextButton) return;

      const hasOverflow = container.scrollWidth > container.clientWidth;
      const maxScrollLeft = Math.max(container.scrollWidth - container.clientWidth, 0);
      const scrollLeft = container.scrollLeft;
      const canScrollLeft = hasOverflow && scrollLeft > 0;
      const canScrollRight = hasOverflow && scrollLeft < maxScrollLeft - 1;

      if (prevButton) {
        const disabled = !canScrollLeft;
        prevButton.disabled = disabled;
      }

      if (nextButton) {
        const disabled = !canScrollRight;
        nextButton.disabled = disabled;
      }
    };

    for (const element of elements) {
      // Avoid attaching multiple listeners to the same element
      if (element.__horizontalWheelScrollInitialized) continue;
      element.__horizontalWheelScrollInitialized = true;

      if (element.children.length > 4) {
        element.classList.add('has-many-items');
      } else {
        element.classList.remove('has-many-items');
      }

      const parent = element.parentElement;
      const prevButton = parent ? parent.querySelector('[data-horizontal-wheel-prev]') : null;
      const nextButton = parent ? parent.querySelector('[data-horizontal-wheel-next]') : null;
      const dragState = {
        pointerId: null,
        startX: 0,
        startY: 0,
        startScrollLeft: 0,
        isPointerDown: false,
        isDragging: false,
        suppressClick: false,
      };

      const hasHorizontalOverflow = () => element.scrollWidth > element.clientWidth;

      const resetDragState = () => {
        dragState.pointerId = null;
        dragState.isPointerDown = false;
        dragState.isDragging = false;
        element.classList.remove('is-horizontal-dragging');
      };

      const shouldIgnorePointerDown = (event) => {
        if (!hasHorizontalOverflow()) return true;
        if (event.pointerType === 'mouse' && event.button !== 0) return true;

        const interactiveTarget = event.target.closest(
          'input, select, textarea, label, summary, [data-no-drag-scroll]'
        );

        return Boolean(interactiveTarget);
      };

      element.addEventListener('pointerdown', (event) => {
        if (shouldIgnorePointerDown(event)) return;

        dragState.pointerId = event.pointerId;
        dragState.startX = event.clientX;
        dragState.startY = event.clientY;
        dragState.startScrollLeft = element.scrollLeft;
        dragState.isPointerDown = true;
        dragState.isDragging = false;
        // Pointer capture is deferred until a drag is confirmed in pointermove.
        // Calling it eagerly on pointerdown causes the browser to redirect the
        // subsequent click to the capturing element instead of the child that was
        // actually tapped, which breaks click-to-flip cards and similar interactions.
      });

      element.addEventListener('pointermove', (event) => {
        if (!dragState.isPointerDown || dragState.pointerId !== event.pointerId) return;

        const deltaX = event.clientX - dragState.startX;
        const deltaY = event.clientY - dragState.startY;

        if (!dragState.isDragging) {
          if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
          if (Math.abs(deltaY) > Math.abs(deltaX)) {
            resetDragState();
            return;
          }

          dragState.isDragging = true;
          dragState.suppressClick = true;
          element.classList.add('is-horizontal-dragging');

          // Only capture the pointer once we know it's a real drag. This keeps
          // simple taps targeting the child element (so their click handlers fire)
          // while still letting drags continue outside the container bounds.
          if (typeof element.setPointerCapture === 'function') {
            element.setPointerCapture(event.pointerId);
          }
        }

        event.preventDefault();
        element.scrollLeft = dragState.startScrollLeft - deltaX;
      });

      const endDrag = (event) => {
        if (dragState.pointerId !== event.pointerId) return;

        if (typeof element.releasePointerCapture === 'function' && element.hasPointerCapture(event.pointerId)) {
          element.releasePointerCapture(event.pointerId);
        }

        resetDragState();
      };

      element.addEventListener('pointerup', endDrag);
      element.addEventListener('pointercancel', endDrag);
      element.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse' || !dragState.isDragging) return;
        endDrag(event);
      });

      element.addEventListener(
        'click',
        (event) => {
          if (!dragState.suppressClick) return;

          dragState.suppressClick = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      element.addEventListener('dragstart', (event) => {
        if (event.target instanceof Element && event.target.closest('img')) {
          event.preventDefault();
        }
      });

      if (prevButton || nextButton) {
        element.addEventListener('scroll', () => {
          updateButtonsState(element, prevButton, nextButton);
        });

        const scrollByAmount = (direction) => {
          const amount = element.clientWidth * 0.8 || 200;
          element.scrollBy({
            left: direction * amount,
            behavior: 'smooth',
          });
        };

        if (prevButton) {
          prevButton.addEventListener('click', () => {
            if (prevButton.disabled) return;
            scrollByAmount(-1);
          });
        }

        if (nextButton) {
          nextButton.addEventListener('click', () => {
            if (nextButton.disabled) return;
            scrollByAmount(1);
          });
        }

        updateButtonsState(element, prevButton, nextButton);
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initHorizontalWheelScroll();
    });
  } else {
    initHorizontalWheelScroll();
  }
})();
